/**
 * Color Palettes for Insights Graphs
 * 10 palettes for light mode, 10 for dark mode.
 * Dark: 5 neon/cyberpunk + 5 mellow/soft
 *
 * Node types: org, asset, tech, cve, cwe, capec, ttp, apt
 */

export const LIGHT_PALETTES = {
  default: {
    name: 'Classic',
    colors: {
      org: '#6366f1',
      asset: '#10b981',
      tech: '#f97316',
      cve: '#e53935',
      cwe: '#9c27b0',
      capec: '#00897b',
      ttp: '#1e88e5',
      apt: '#f59e0b',
    },
  },
  ocean: {
    name: 'Ocean',
    colors: {
      org: '#0077b6',
      asset: '#00b4d8',
      tech: '#0096c7',
      cve: '#d62828',
      cwe: '#6a4c93',
      capec: '#1b9aaa',
      ttp: '#023e8a',
      apt: '#e76f51',
    },
  },
  forest: {
    name: 'Forest',
    colors: {
      org: '#2d6a4f',
      asset: '#40916c',
      tech: '#b5838d',
      cve: '#c1121f',
      cwe: '#7b2cbf',
      capec: '#52796f',
      ttp: '#3a5a40',
      apt: '#bc6c25',
    },
  },
  sunset: {
    name: 'Sunset',
    colors: {
      org: '#7209b7',
      asset: '#f72585',
      tech: '#ff6d00',
      cve: '#d00000',
      cwe: '#3a0ca3',
      capec: '#4361ee',
      ttp: '#560bad',
      apt: '#e85d04',
    },
  },
  earth: {
    name: 'Earth',
    colors: {
      org: '#5c4742',
      asset: '#8cb369',
      tech: '#f4a259',
      cve: '#bc4749',
      cwe: '#7a6c5d',
      capec: '#3a7d44',
      ttp: '#264653',
      apt: '#e76f51',
    },
  },
  berry: {
    name: 'Berry',
    colors: {
      org: '#7c3aed',
      asset: '#db2777',
      tech: '#ea580c',
      cve: '#dc2626',
      cwe: '#9333ea',
      capec: '#0d9488',
      ttp: '#4f46e5',
      apt: '#c2410c',
    },
  },
  slate: {
    name: 'Slate',
    colors: {
      org: '#475569',
      asset: '#0891b2',
      tech: '#d97706',
      cve: '#be123c',
      cwe: '#7c3aed',
      capec: '#0f766e',
      ttp: '#1d4ed8',
      apt: '#b45309',
    },
  },
  coral: {
    name: 'Coral',
    colors: {
      org: '#e11d48',
      asset: '#06b6d4',
      tech: '#f59e0b',
      cve: '#b91c1c',
      cwe: '#8b5cf6',
      capec: '#14b8a6',
      ttp: '#2563eb',
      apt: '#ea580c',
    },
  },
  meadow: {
    name: 'Meadow',
    colors: {
      org: '#4338ca',
      asset: '#059669',
      tech: '#d97706',
      cve: '#dc2626',
      cwe: '#7e22ce',
      capec: '#0d9488',
      ttp: '#1e40af',
      apt: '#ca8a04',
    },
  },
  vintage: {
    name: 'Vintage',
    colors: {
      org: '#78350f',
      asset: '#4d7c0f',
      tech: '#b45309',
      cve: '#9f1239',
      cwe: '#6b21a8',
      capec: '#115e59',
      ttp: '#1e3a5f',
      apt: '#92400e',
    },
  },
};

// Dark palettes: first 5 are neon/cyberpunk, last 5 are mellow/soft
export const DARK_PALETTES = {
  // ── Neon / Cyberpunk ──
  default: {
    name: 'Neon',
    colors: {
      org: '#00f5ff',
      asset: '#34d399',
      tech: '#fb923c',
      cve: '#ffcc00',
      cwe: '#e040fb',
      capec: '#1de9b6',
      ttp: '#ff00aa',
      apt: '#ff3355',
    },
  },
  cyber: {
    name: 'Cyber',
    colors: {
      org: '#00e5ff',
      asset: '#76ff03',
      tech: '#ffab40',
      cve: '#ff1744',
      cwe: '#d500f9',
      capec: '#64ffda',
      ttp: '#448aff',
      apt: '#ff6e40',
    },
  },
  synthwave: {
    name: 'Synthwave',
    colors: {
      org: '#f72585',
      asset: '#4cc9f0',
      tech: '#ffd60a',
      cve: '#ff006e',
      cwe: '#8338ec',
      capec: '#3a86ff',
      ttp: '#b5179e',
      apt: '#fb5607',
    },
  },
  hotline: {
    name: 'Hotline',
    colors: {
      org: '#ff2ecc',
      asset: '#00ffab',
      tech: '#ffe600',
      cve: '#ff003c',
      cwe: '#bf00ff',
      capec: '#00ffd5',
      ttp: '#ff6ec7',
      apt: '#ff8c00',
    },
  },
  matrix: {
    name: 'Matrix',
    colors: {
      org: '#39ff14',
      asset: '#00ff41',
      tech: '#ccff00',
      cve: '#ff2400',
      cwe: '#00ff80',
      capec: '#7fff00',
      ttp: '#32cd32',
      apt: '#ffbf00',
    },
  },
  // ── Mellow / Soft ──
  frost: {
    name: 'Frost',
    colors: {
      org: '#88c0d0',
      asset: '#a3be8c',
      tech: '#ebcb8b',
      cve: '#bf616a',
      cwe: '#b48ead',
      capec: '#8fbcbb',
      ttp: '#81a1c1',
      apt: '#d08770',
    },
  },
  aurora: {
    name: 'Aurora',
    colors: {
      org: '#80d8ff',
      asset: '#a5d6a7',
      tech: '#ffcc80',
      cve: '#ef9a9a',
      cwe: '#ce93d8',
      capec: '#80cbc4',
      ttp: '#90caf9',
      apt: '#ffab91',
    },
  },
  dusk: {
    name: 'Dusk',
    colors: {
      org: '#9fa8da',
      asset: '#a5d6a7',
      tech: '#ffe082',
      cve: '#ef9a9a',
      cwe: '#b39ddb',
      capec: '#80cbc4',
      ttp: '#7986cb',
      apt: '#ffcc80',
    },
  },
  pastel: {
    name: 'Pastel',
    colors: {
      org: '#a0c4ff',
      asset: '#9bf6ff',
      tech: '#ffd6a5',
      cve: '#ffadad',
      cwe: '#bdb2ff',
      capec: '#caffbf',
      ttp: '#a0c4ff',
      apt: '#fdffb6',
    },
  },
  arctic: {
    name: 'Arctic',
    colors: {
      org: '#7dd3fc',
      asset: '#86efac',
      tech: '#fde68a',
      cve: '#fca5a5',
      cwe: '#d8b4fe',
      capec: '#99f6e4',
      ttp: '#93c5fd',
      apt: '#fed7aa',
    },
  },
};

const LIGHT_PALETTE_STORAGE_KEY = 'cynergy-color-palette-light';
const DARK_PALETTE_STORAGE_KEY = 'cynergy-color-palette-dark';

/**
 * Get the active palette colors for the given theme mode.
 */
export function getPaletteColors(isDark, paletteId = 'default') {
  const palettes = isDark ? DARK_PALETTES : LIGHT_PALETTES;
  const palette = palettes[paletteId] || palettes.default;
  return { ...palette.colors };
}

/**
 * Get all available palettes for the given theme mode.
 */
export function getAvailablePalettes(isDark) {
  return isDark ? DARK_PALETTES : LIGHT_PALETTES;
}

/**
 * Load saved palette IDs from localStorage.
 * @returns {{ light: string, dark: string }}
 */
export function loadSavedPalettes() {
  try {
    return {
      light: localStorage.getItem(LIGHT_PALETTE_STORAGE_KEY) || 'default',
      dark: localStorage.getItem(DARK_PALETTE_STORAGE_KEY) || 'default',
    };
  } catch {
    return {
      light: 'default',
      dark: 'default',
    };
  }
}

/**
 * Save a palette ID to localStorage for a specific mode.
 * @param {'light'|'dark'} mode
 * @param {string} paletteId
 */
export function savePalette(mode, paletteId) {
  try {
    const key = mode === 'dark' ? DARK_PALETTE_STORAGE_KEY : LIGHT_PALETTE_STORAGE_KEY;
    localStorage.setItem(key, paletteId);
  } catch {
    // Silently fail
  }
}

/**
 * Apply palette colors as CSS custom properties on the document root.
 */
export function applyPaletteToCSS(isDark, paletteId = 'default') {
  const colors = getPaletteColors(isDark, paletteId);
  const root = document.documentElement;
  const bgOpacity = isDark ? 0.2 : 0.15;

  const cssMap = {
    '--node-org': colors.org,
    '--node-asset': colors.asset,
    '--node-tech': colors.tech,
    '--node-cve': colors.cve,
    '--node-cwe': colors.cwe,
    '--node-capec': colors.capec,
    '--node-ttp': colors.ttp,
    '--node-apt': colors.apt,
    '--node-technique': colors.ttp,
    '--node-default': isDark ? '#666' : '#999',
  };

  Object.entries(cssMap).forEach(([prop, value]) => {
    root.style.setProperty(prop, value);
  });

  Object.entries(colors).forEach(([type, color]) => {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    root.style.setProperty(`--node-${type}-bg`, `rgba(${r}, ${g}, ${b}, ${bgOpacity})`);
  });
}
